import type { OrderDetails } from '@/entities/order/model'
import type { CrateType } from '@/features/order/model/orderDetails.types'
import { Box, Flex, Group, Paper, Space, Stack, Text } from '@mantine/core'
import { IconChevronDown } from '@tabler/icons-react'
import clsx from 'clsx'
import { getEmoji, getVanSeq } from '@/entities/order/lib'
import { OrderMetaInfoCheck, OrderMetaInfoVehicle, OrderStatusDisplay } from '@/entities/order/ui'
import { useCrateEmojisEnabled } from '@/entities/state/model'
import { getOrderSecondaryMessage, getOrderTypeWithCollectionWindow, getRapidOrderStatus, getRestrictedItemsDisplay, useOrderAlert } from '@/features/order/lib'
import { formatTime } from '@/shared/lib/dayjs'
import { normaliseEmojiItems } from '@/shared/lib/emoji'
import { OrderCrateScanRows } from '../crate-scan-rows/OrderCrateScanRows'
import { StatusUpdatePopover } from '../status-update/StatusUpdatePopover'
import classes from './OrderDetailsDesktopLayout.module.css'

const EMPTY_MISSING_CRATE_TYPES: string[] = []

interface OrderDetailsDesktopLayoutProps {
  order: OrderDetails
  crateTypes: CrateType[]
  totalCrates: number
  onStatusUpdate?: (status: string) => void
  withoutBackdrop?: boolean
  scannedCrates?: number
  showScanProgress?: boolean
  /** BD-8588: replace crate list with licence-plate waiting message */
  showLicencePlateWaitingCard?: boolean
  licencePlateWaitingMessage?: string
  missingCrateTypes?: string[]
  /** BD-8595: show missing-mark underlines during the onboarding tutorial. */
  enableMissingMarking?: boolean
}

export function OrderDetailsDesktopLayout({
  order,
  crateTypes,
  totalCrates,
  onStatusUpdate,
  withoutBackdrop,
  scannedCrates = 0,
  showScanProgress = false,
  showLicencePlateWaitingCard = false,
  licencePlateWaitingMessage,
  missingCrateTypes = EMPTY_MISSING_CRATE_TYPES,
  enableMissingMarking = false,
}: OrderDetailsDesktopLayoutProps) {
  const { displayState } = useOrderAlert(order)
  const { statusText, statusDisplayState, isRapidOrderContext } = getRapidOrderStatus({
    order,
    displayState,
    withoutBackdrop,
  })

  const { vanNumber, sequenceNumber } = getVanSeq(order.hsFields)
  const crateEmojisEnabled = useCrateEmojisEnabled()
  const emojiItems = normaliseEmojiItems(getEmoji(order.hsFields))

  return (
    <Flex className={classes.contentLayout}>
      <Paper className={classes.orderInfoCard}>
        <Flex direction="column" justify="space-between" gap="xl" h="auto">
          <Group justify="space-between" align="flex-start" wrap="nowrap" w="100%">
            <Stack gap="6px">
              <Text span className={classes.entryLabel}>Order number</Text>
              <Text span className={classes.entryValue}>{order.orderId}</Text>
            </Stack>

            <OrderStatusDisplay
              orderStatus={statusText}
              displayState={statusDisplayState}
              secondaryText={isRapidOrderContext ? undefined : getOrderSecondaryMessage(
                order.status,
                order.lastTransitionTime ? formatTime(order.lastTransitionTime, 'HH:mm') : undefined,
              )}
              eta={order.eta}
              etaDirection={order.etaDirection}
              scheduledTimeForCollection={order.scheduledTimeForCollection}
            />
          </Group>

          <Group gap="sm">
            {(vanNumber || sequenceNumber) && (
              <OrderMetaInfoVehicle
                vanNumber={vanNumber}
                sequenceNumber={sequenceNumber}
                size="lg"
                bg="gray.1"
              />
            )}

            {(emojiItems.length > 0) && (
              <OrderMetaInfoCheck
                items={emojiItems}
                size="lg"
                bg="gray.1"
              />
            )}
          </Group>

          <StatusUpdatePopover
            orderId={order.orderId}
            currentStatus={order.status}
            lastUpdateTime={order.lastUpdateTime}
            onStatusUpdate={onStatusUpdate}
            renderTrigger={({ onClick, isOpen }) => (
              <Box
                className={classes.statusTrigger}
                onClick={onClick}
                data-onboarding="order-status-trigger"
              >
                <Text span className={classes.entryLabel}>Status</Text>
                <Group gap="xs">
                  <Text className={classes.statusItem}>{displayState?.stateDisplayName || order.status}</Text>
                  <IconChevronDown
                    size={24}
                    className={`${classes.statusIcon} ${isOpen ? classes.statusIconRotated : classes.statusIconDefault}`}
                  />
                </Group>
              </Box>
            )}
          />

          <Stack gap="6px">
            <Text span className={classes.entryLabel}>Type</Text>
            <Text span className={classes.entryValue}>
              {getOrderTypeWithCollectionWindow(order.hsFields)}
            </Text>
          </Stack>

          <Stack gap="6px">
            <Text span className={classes.entryLabel}>Restricted items</Text>
            <Text span className={classes.entryValue}>
              {getRestrictedItemsDisplay(order.hsFields)}
            </Text>
          </Stack>
        </Flex>
      </Paper>

      <Paper
        className={classes.crateCard}
        data-testid={showLicencePlateWaitingCard ? 'licence-plate-waiting-card' : undefined}
        data-onboarding="tutorial-crate-progress"
      >
        <Group grow justify="space-between" p="md" bg="gray.9" className={clsx(classes.crateHeader, classes.crateRow)}>
          {showLicencePlateWaitingCard
            ? <Space />
            : <Text span className={classes.crateItemType}>Total crates</Text>}
          {showLicencePlateWaitingCard
            ? <Space />
            : (
                <Text span className={classes.crateItemCount}>
                  {showScanProgress ? `${scannedCrates}/${totalCrates}` : totalCrates}
                </Text>
              )}
          <Space />
        </Group>

        <Box p="lg">
          {showLicencePlateWaitingCard
            ? (
                <Text span className={classes.licencePlateWaitingMessage}>
                  {licencePlateWaitingMessage}
                </Text>
              )
            : (
                <OrderCrateScanRows
                  crateTypes={crateTypes.map(crate => ({
                    ...crate,
                    icon: crateEmojisEnabled ? crate.icon : '',
                  }))}
                  showScanProgress={showScanProgress}
                  enableMissingMarking={enableMissingMarking}
                  missingCrateTypes={missingCrateTypes}
                  crateItemTypeClassName={classes.crateItemType}
                  crateItemCountClassName={classes.crateItemCount}
                  crateItemGroupClassName={classes.crateItemGroup}
                  rowClassName={classes.crateRow}
                />
              )}
        </Box>
      </Paper>
    </Flex>
  )
}
